import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ChevronRight } from "lucide-react";
import { STAGES, StageSelect, money } from "@/components/mobile/MobileDeals";

export default function MobilePipeline() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("Lead");

  useEffect(() => {
    api.get("/crm/deals").then(({ data }) => setDeals(data.deals))
      .catch(() => toast.error("Could not load deals")).finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => Object.fromEntries(STAGES.map((s) => {
    const ds = deals.filter((d) => d.stage === s);
    return [s, { count: ds.length, total: ds.reduce((a, d) => a + (Number(d.amount) || 0), 0) }];
  })), [deals]);
  const shown = deals.filter((d) => d.stage === stage);

  return (
    <div data-testid="m-pipeline">
      <div className="sticky top-[var(--m-header-h,60px)] z-20 bg-[#F4F4F5] pt-4 pb-3">
        <div className="flex gap-2 overflow-x-auto px-4 no-scrollbar">
          {STAGES.map((s) => (
            <button key={s} onClick={() => setStage(s)} data-testid={`m-stage-${s.toLowerCase()}`}
              className={`shrink-0 px-3 py-2 text-left border ${stage === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-zinc-600 border-zinc-300"}`}>
              <div className="text-xs font-semibold">{s} <span className={stage === s ? "text-[#D4AF37]" : "text-zinc-400"}>{stats[s].count}</span></div>
              <div className="text-[10px] opacity-70">{money(stats[s].total) || "$0"}</div>
            </button>
          ))}
        </div>
      </div>
      {loading ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div> : (
        <div className="px-4 space-y-2">
          {shown.length === 0 && <p className="py-10 text-center text-sm text-zinc-500" data-testid="m-pipeline-empty">No deals in {stage}.</p>}
          {shown.map((d) => (
            <div key={d.id} className="bg-white border border-zinc-200 border-l-4 border-l-[#D4AF37] p-3" data-testid={`m-pdeal-${d.id}`}>
              <button onClick={() => navigate(`/mobilecrm/customers/${d.customer_id}`)} className="w-full text-left flex items-center gap-2" data-testid={`m-pdeal-open-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#0F172A] truncate">{d.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{d.company_name || "Customer"}{d.amount != null ? ` · ${money(d.amount)}` : ""}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-300" />
              </button>
              <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                Move to <StageSelect deal={d} testid={`m-pdeal-stage-${d.id}`} onMoved={(s) => setDeals((xs) => xs.map((x) => (x.id === d.id ? { ...x, stage: s } : x)))} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
